/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — tb-editor.ts
   Trial Balance Editor: build, edit, delete, add, undo/redo,
   prior year fields, balance check, event delegation
   ═══════════════════════════════════════════════════════════════════════ */

import {
  state,
  SECTIONS,
  SECTION_SIGN,
  SECTION_LABELS,
  type SectionData,
  type LineItem,
} from './state';
import { esc, fmt, el, elInput } from './utils';
import { saveProject } from './data';

// ─── Local Types ───────────────────────────────────────────────────────────

interface PYField {
  id: string;
  section: string;
  label: string;
}

// Section labels imported from state.ts (SECTION_LABELS)

// ─── TB Editor Undo/Redo ──────────────────────────────────────────────────
interface TBSnapshot { currentData: SectionData; priorData: SectionData; }
const tbUndoStack: TBSnapshot[] = [];
const tbRedoStack: TBSnapshot[] = [];
const MAX_UNDO = 30;

function cloneData(d: SectionData): SectionData {
  const c: SectionData = {};
  for (const k in d) { c[k] = d[k].map(item => ({ ...item })); }
  return c;
}

export function pushTBUndo(): void {
  tbUndoStack.push({ currentData: cloneData(state.currentData), priorData: cloneData(state.priorData) });
  if (tbUndoStack.length > MAX_UNDO) tbUndoStack.shift();
  tbRedoStack.length = 0; // clear redo on new edit
}

export function tbUndo(): void {
  if (tbUndoStack.length === 0) return;
  tbRedoStack.push({ currentData: cloneData(state.currentData), priorData: cloneData(state.priorData) });
  const snap = tbUndoStack.pop()!;
  state.currentData = snap.currentData;
  state.priorData = snap.priorData;
  saveProject();
  buildTrialBalanceEditor();
}

export function tbRedo(): void {
  if (tbRedoStack.length === 0) return;
  tbUndoStack.push({ currentData: cloneData(state.currentData), priorData: cloneData(state.priorData) });
  const snap = tbRedoStack.pop()!;
  state.currentData = snap.currentData;
  state.priorData = snap.priorData;
  saveProject();
  buildTrialBalanceEditor();
}

// ─── Feature 3: Trial Balance Editor ────────────────────────────────────────

export function buildTrialBalanceEditor(): void {
  const container = el('tb-editor-content');
  const card = el('tb-editor-card');
  if (!container || !card) return;
  let hasData = false;
  SECTIONS.forEach(function(s: string) { if (state.currentData[s] && state.currentData[s].length > 0) hasData = true; });
  if (!hasData) {
    container.innerHTML = '<p style="font-size:0.85rem;color:var(--muted);margin-bottom:14px">No trial balance data imported yet. Import a CSV above to populate this editor.</p>';
    if (!card.classList.contains('collapsed')) card.classList.add('collapsed');
    return;
  }
  card.classList.remove('collapsed');
  let html = '<table class="tb-editor-table" role="grid" aria-label="Trial Balance Editor"><thead><tr><th>Section</th><th>Account Name</th><th>Amount</th><th style="width:80px">Actions</th></tr></thead><tbody>';
  let grandTotal = 0;
  SECTIONS.forEach(function(sec: string) {
    const items = state.currentData[sec];
    if (!items || items.length === 0) return;
    html += '<tr class="tb-section-header"><td colspan="4">' + esc(SECTION_LABELS[sec] || sec) + '</td></tr>';
    let secTotal = 0;
    items.forEach(function(item: LineItem, idx: number) {
      const amt = parseFloat(String(item.amount)) || 0;
      secTotal += amt;
      html += '<tr class="tb-item" data-section="' + sec + '" data-idx="' + idx + '" tabindex="0" role="row">';
      html += '<td style="font-size:0.78rem;color:var(--muted)">' + esc(SECTION_LABELS[sec] || sec) + '</td>';
      html += '<td><span class="tb-label-display">' + esc(item.label) + '</span></td>';
      html += '<td style="text-align:right"><span class="tb-amt-display">' + fmt(amt) + '</span></td>';
      html += '<td style="text-align:center">';
      html += '<button class="btn btn-ghost btn-sm" data-tb-action="edit" data-section="' + sec + '" data-idx="' + idx + '" title="Edit" aria-label="Edit ' + esc(item.label) + '" style="padding:3px 7px;font-size:0.75rem">&#9998;</button> ';
      html += '<button class="btn btn-danger btn-sm" data-tb-action="delete" data-section="' + sec + '" data-idx="' + idx + '" title="Delete" aria-label="Delete ' + esc(item.label) + '" style="padding:3px 7px;font-size:0.75rem">&times;</button>';
      html += '</td></tr>';
    });
    html += '<tr class="tb-total"><td></td><td style="text-align:right;font-size:0.82rem">Section Total:</td><td style="text-align:right">' + fmt(secTotal) + '</td><td></td></tr>';
    grandTotal += secTotal;
  });
  html += '<tr class="tb-add-row"><td><select id="tb-add-section"><option value="">Section...</option>';
  SECTIONS.forEach(function(s: string) { html += '<option value="' + s + '">' + esc(SECTION_LABELS[s] || s) + '</option>'; });
  html += '</select></td><td><input type="text" id="tb-add-label" placeholder="Account name"></td>';
  html += '<td><input type="number" id="tb-add-amount" step="0.01" placeholder="0.00"></td>';
  html += '<td style="text-align:center"><button class="btn btn-primary btn-sm" data-tb-action="add" style="padding:4px 10px;font-size:0.78rem">Add</button></td></tr>';
  html += '<tr class="tb-grand-total"><td></td><td style="text-align:right">Grand Total:</td><td style="text-align:right">' + fmt(grandTotal) + '</td><td></td></tr>';
  html += '</tbody></table>';
  let totalItems = 0;
  SECTIONS.forEach(function(s: string) { totalItems += (state.currentData[s] ? state.currentData[s].length : 0); });
  html += '<div class="tb-summary"><span><strong>' + totalItems + '</strong> line items</span><span>Grand Total: <strong>' + fmt(grandTotal) + '</strong></span></div>';
  html += '<div style="margin-top:14px;display:flex;gap:8px;align-items:center">' +
    '<button class="btn btn-primary btn-sm" data-action="generateAll">Refresh Statements</button>' +
    '<button class="btn btn-ghost btn-sm" data-action="tbUndo" title="Undo (Ctrl+Z)" aria-label="Undo">&#8630; Undo</button>' +
    '<button class="btn btn-ghost btn-sm" data-action="tbRedo" title="Redo (Ctrl+Shift+Z)" aria-label="Redo">&#8631; Redo</button>' +
    '</div>';
  container.innerHTML = html;
  renderTBBalanceCheck();
}

// ─── TB Balance Validation Bar ──────────────────────────────────────
export function renderTBBalanceCheck(): void {
  let totalDebits = 0;
  let totalCredits = 0;

  // Sum all current period amounts by section sign
  SECTIONS.forEach(function(s: string) {
    const sign = SECTION_SIGN[s] || 1;
    (state.currentData[s] || []).forEach(function(item: LineItem) {
      const amt = Number(item.amount) || 0;
      const raw = amt * sign; // Convert back to raw debit/credit
      if (raw >= 0) totalDebits += raw;
      else totalCredits += Math.abs(raw);
    });
  });

  const diff = Math.abs(totalDebits - totalCredits);
  const isBalanced = diff < 0.01;

  let existingBar = document.getElementById('tb-balance-bar');
  if (!existingBar) {
    existingBar = document.createElement('div');
    existingBar.id = 'tb-balance-bar';
    const grid = document.getElementById('tb-grid') || document.getElementById('trial-balance-grid');
    if (grid && grid.parentElement) {
      grid.parentElement.insertBefore(existingBar, grid.nextSibling);
    }
  }
  if (existingBar) {
    const color = isBalanced ? 'var(--success, #16a34a)' : 'var(--danger, #dc2626)';
    const bgColor = isBalanced ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.08)';
    const icon = isBalanced ? '\u2713' : '\u26A0';
    const fmtDebits = totalDebits.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const fmtCredits = totalCredits.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const fmtDiff = diff.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    existingBar.style.cssText = 'padding:10px 16px;border-radius:6px;margin-top:8px;font-size:0.88rem;display:flex;justify-content:space-between;align-items:center;background:' + bgColor + ';border:1px solid ' + color + ';color:' + color + ';';
    existingBar.innerHTML = '<span><strong>' + icon + ' ' + (isBalanced ? 'Trial Balance is in balance' : 'Trial Balance is OUT OF BALANCE') + '</strong></span>' +
      '<span>Debits: $' + fmtDebits + ' &nbsp;|&nbsp; Credits: $' + fmtCredits +
      (isBalanced ? '' : ' &nbsp;|&nbsp; Difference: $' + fmtDiff) + '</span>';
  }
}

export function tbEditRow(section: string, idx: number): void {
  const items = state.currentData[section];
  if (!items || !items[idx]) return;
  const item = items[idx];
  const rowEls = document.querySelectorAll('.tb-item[data-section="' + section + '"][data-idx="' + idx + '"]');
  if (rowEls.length === 0) return;
  const row = rowEls[0] as HTMLElement;
  const cells = row.children;
  cells[1].innerHTML = '<input type="text" value="' + esc(item.label) + '" id="tb-edit-label-' + section + '-' + idx + '" style="width:100%">';
  cells[2].innerHTML = '<input type="number" value="' + (item.amount || 0) + '" id="tb-edit-amt-' + section + '-' + idx + '" step="0.01" style="width:120px">';
  cells[3].innerHTML = '<button class="btn btn-primary btn-sm" data-tb-action="save" data-section="' + section + '" data-idx="' + idx + '" style="padding:3px 7px;font-size:0.72rem">Save</button> <button class="btn btn-ghost btn-sm" data-tb-action="cancel" style="padding:3px 7px;font-size:0.72rem">Cancel</button>';
}

export function tbSaveRow(section: string, idx: number): void {
  const labelEl = elInput('tb-edit-label-' + section + '-' + idx);
  const amtEl = elInput('tb-edit-amt-' + section + '-' + idx);
  if (!labelEl || !amtEl) return;
  const label = labelEl.value.trim();
  const amt = parseFloat(amtEl.value) || 0;
  if (!label) { alert('Account name cannot be empty.'); return; }
  pushTBUndo();
  state.currentData[section][idx] = { label: label, amount: amt };
  saveProject(); buildTrialBalanceEditor();
}

export function tbDeleteRow(section: string, idx: number): void {
  if (!confirm('Remove this line item?')) return;
  pushTBUndo();
  state.currentData[section].splice(idx, 1);
  saveProject(); buildTrialBalanceEditor();
}

export function tbAddRow(): void {
  const section = elInput('tb-add-section')?.value ?? '';
  const label = elInput('tb-add-label')?.value.trim() ?? '';
  const amt = parseFloat(elInput('tb-add-amount')?.value ?? '0') || 0;
  if (!section) { alert('Please select a section.'); return; }
  if (!label) { alert('Please enter an account name.'); return; }
  pushTBUndo();
  if (!state.currentData[section]) state.currentData[section] = [];
  state.currentData[section].push({ label: label, amount: amt });
  saveProject(); buildTrialBalanceEditor();
}

// ─── Feature 1: Prior Year Balances ─────────────────────────────────────────

const PY_FIELDS: PYField[] = [
  { id: 'py-current-assets', section: 'current-assets', label: 'Prior Year Current Assets' },
  { id: 'py-noncurrent-assets', section: 'noncurrent-assets', label: 'Prior Year Non-Current Assets' },
  { id: 'py-current-liab', section: 'current-liab', label: 'Prior Year Current Liabilities' },
  { id: 'py-noncurrent-liab', section: 'noncurrent-liab', label: 'Prior Year Non-Current Liabilities' },
  { id: 'py-equity', section: 'equity', label: 'Prior Year Equity' },
  { id: 'py-revenue', section: 'revenue', label: 'Prior Year Revenue' },
  { id: 'py-cogs', section: 'cogs', label: 'Prior Year COGS' },
  { id: 'py-opex', section: 'opex', label: 'Prior Year Operating Expenses' },
  { id: 'py-other', section: 'other', label: 'Prior Year Other Income/Expense' },
  { id: 'py-cf-operating', section: 'cf-operating', label: 'Prior Year Cash from Operations' },
  { id: 'py-cf-investing', section: 'cf-investing', label: 'Prior Year Cash from Investing' },
  { id: 'py-cf-financing', section: 'cf-financing', label: 'Prior Year Cash from Financing' },
];

function checkPriorDataExists(): void {
  let hasData = false;
  SECTIONS.forEach(function(s: string) { if (state.priorData[s] && state.priorData[s].length > 0) hasData = true; });
  const warn = document.getElementById('py-overwrite-warning');
  if (warn) warn.style.display = hasData ? 'block' : 'none';
}

export function savePriorYearBalances(): void {
  PY_FIELDS.forEach(function(f: PYField) {
    const fieldEl = elInput(f.id);
    const val = parseFloat(fieldEl ? fieldEl.value : '0') || 0;
    state.priorData[f.section] = val !== 0 ? [{ label: f.label, amount: val }] : [];
  });
  saveProject();
  const statusEl = document.getElementById('py-status');
  if (statusEl) {
    statusEl.innerHTML = '<div class="import-status success">Prior year balances saved successfully.</div>';
    setTimeout(function() { statusEl.innerHTML = ''; }, 3000);
  }
  checkPriorDataExists();
}

export function clearPriorYearBalances(): void {
  if (!confirm('Clear all prior year balance data?')) return;
  SECTIONS.forEach(function(s: string) { state.priorData[s] = []; });
  PY_FIELDS.forEach(function(f: PYField) { const fieldEl = elInput(f.id); if (fieldEl) fieldEl.value = ''; });
  saveProject();
  const statusEl = document.getElementById('py-status');
  if (statusEl) {
    statusEl.innerHTML = '<div class="import-status info">Prior year balances cleared.</div>';
    setTimeout(function() { statusEl.innerHTML = ''; }, 3000);
  }
  checkPriorDataExists();
}

export function loadPriorYearFields(): void {
  PY_FIELDS.forEach(function(f: PYField) {
    const fieldEl = elInput(f.id);
    if (!fieldEl) return;
    if (state.priorData[f.section] && state.priorData[f.section].length > 0) {
      let total = 0;
      state.priorData[f.section].forEach(function(item: LineItem) { total += parseFloat(String(item.amount)) || 0; });
      fieldEl.value = String(total || '');
    } else { fieldEl.value = ''; }
  });
  checkPriorDataExists();
}

// ---------------------------------------------------------------------------
// Event Delegation — Trial Balance Editor
// ---------------------------------------------------------------------------

document.addEventListener('click', function (e: MouseEvent) {
  const target = (e.target as HTMLElement).closest('[data-tb-action]') as HTMLElement | null;
  if (!target) return;

  const action = target.dataset.tbAction;
  const section = target.dataset.section;
  const idx = target.dataset.idx !== undefined ? parseInt(target.dataset.idx, 10) : -1;

  switch (action) {
    case 'edit':
      if (section && idx >= 0) tbEditRow(section, idx);
      break;
    case 'delete':
      if (section && idx >= 0) tbDeleteRow(section, idx);
      break;
    case 'save':
      if (section && idx >= 0) tbSaveRow(section, idx);
      break;
    case 'cancel':
      buildTrialBalanceEditor();
      break;
    case 'add':
      tbAddRow();
      break;
  }
});
